/**
 * Chaînes (chrome) + ancre du formulaire de capture de lead réutilisable
 * QuickLeadForm, par locale. Module NEUTRE (ni "use client" ni serveur).
 *
 * Pourquoi neutre : l'ancre de section QUICK_LEAD_FORM_ID est importée À LA FOIS
 * par le formulaire client (QuickLeadForm.tsx) ET par des vues SERVEUR
 * (ChauffeurSlugView, CityRentalView, PrestigeRentalView…) qui repointent leur
 * CTA vers `#demande`. Un `export const` depuis un fichier "use client" devient
 * une référence client côté serveur (se rend en `#function(){throw…}` au lieu de
 * "demande"). À garder ici — règle RSC. L'audit full-site ne l'attrape pas.
 *
 * Sépare aussi le texte UI du contenu éditorial (slugs/dicts de page) pour que
 * les vues FR et EN partagent le même markup : zéro doublon, zéro FR codé en dur
 * côté EN. Le formulaire est bas-de-funnel et bas-friction (compact), partagé
 * par les pages chauffeur (VTC) et location voiture via la prop `context`.
 */
import { type I18n } from "@/config/localeText";

/**
 * Ancre de section du formulaire (cible des CTA hero / bas de page).
 * Définie ICI (module neutre) pour être importée par la vue serveur ET le
 * formulaire client sans devenir une référence client. Voir l'en-tête.
 */
export const QUICK_LEAD_FORM_ID = "demande";

/** Contexte d'usage : pilote sujet équipe, locationType et quelques libellés. */
export type QuickLeadContext = "chauffeur" | "location";

export type QuickLeadUiStrings = {
  /** En-tête de section. */
  eyebrow: string;
  /**
   * Titre — varie selon le contexte (VTC vs location voiture). `serviceLabel`
   * (ville / page) est injecté par le composant.
   */
  heading: (context: QuickLeadContext, serviceLabel?: string) => string;
  /** Sous-titre — varie aussi selon le contexte. */
  subtitle: (context: QuickLeadContext, serviceLabel?: string) => string;
  /** Pastille de réassurance contextuelle (à côté du titre). */
  badge: (context: QuickLeadContext, serviceLabel?: string) => string;
  /** Coordonnées. */
  firstNameLabel: string;
  firstNamePlaceholder: string;
  lastNameLabel: string;
  lastNamePlaceholder: string;
  emailLabel: string;
  emailPlaceholder: string;
  phoneLabel: string;
  phonePlaceholder: string;
  /** Date souhaitée (facultative). */
  dateLabel: string;
  /** Besoin libre (facultatif) — remplace les passagers (bas-friction). */
  needLabel: (context: QuickLeadContext) => string;
  needPlaceholder: (context: QuickLeadContext) => string;
  /** Message (facultatif). */
  messageLabel: string;
  messagePlaceholder: (context: QuickLeadContext) => string;
  /**
   * Message par défaut composé si la zone de message est vide
   * (≥ 5 car. requis côté API). Reprend le contexte / service + besoin + date.
   */
  defaultMessage: (input: {
    context: QuickLeadContext;
    serviceLabel?: string;
    need: string;
    date: string;
  }) => string;
  honeypotLabel: string;
  /** Bouton + états. */
  submit: string;
  sending: string;
  successTitle: string;
  successMessage: string;
  errorMessage: string;
  /** Réassurance RGPD / confidentialité + consentement. */
  privacyNote: string;
  consentNote: string;
  /** Libellés requis (validation client). */
  requiredFirstName: string;
  requiredLastName: string;
  requiredEmail: string;
  invalidPhone: string;
};

export const QUICK_LEAD_UI: I18n<QuickLeadUiStrings> = {
  fr: {
    eyebrow: "Réponse rapide — sans engagement",
    heading: (context, serviceLabel) =>
      context === "chauffeur"
        ? serviceLabel
          ? `Réservez votre chauffeur privé — ${serviceLabel}`
          : "Réservez votre chauffeur privé"
        : serviceLabel
          ? `Demandez votre location — ${serviceLabel}`
          : "Demandez votre location",
    subtitle: (context) =>
      context === "chauffeur"
        ? "Dites-nous votre besoin (date, trajet ou occasion) : notre équipe vous recontacte rapidement avec une proposition claire, chauffeur professionnel et tarif sans surprise."
        : "Indiquez votre besoin et vos dates : notre équipe vous recontacte rapidement avec les véhicules disponibles et un tarif clair, livraison possible sur votre lieu de séjour.",
    badge: (context) =>
      context === "chauffeur"
        ? "Chauffeur professionnel · Côte d'Azur"
        : "Premium & prestige · livraison possible",
    firstNameLabel: "Prénom",
    firstNamePlaceholder: "Votre prénom",
    lastNameLabel: "Nom",
    lastNamePlaceholder: "Votre nom",
    emailLabel: "Email",
    emailPlaceholder: "vous@email.com",
    phoneLabel: "Téléphone (facultatif)",
    phonePlaceholder: "+33 6 12 34 56 78",
    dateLabel: "Date souhaitée (facultatif)",
    needLabel: (context) =>
      context === "chauffeur" ? "Votre besoin" : "Véhicule ou besoin",
    needPlaceholder: (context) =>
      context === "chauffeur"
        ? "Ex. : transfert aéroport, mise à disposition, événement…"
        : "Ex. : SUV premium, cabriolet, durée, type d'usage…",
    messageLabel: "Message (facultatif)",
    messagePlaceholder: (context) =>
      context === "chauffeur"
        ? "Précisez vos horaires, le nombre de passagers, les bagages, le contexte…"
        : "Précisez vos dates, le lieu de remise, le type d'usage, vos préférences…",
    defaultMessage: ({ context, serviceLabel, need, date }) => {
      const base =
        context === "chauffeur"
          ? `Demande chauffeur privé${serviceLabel ? ` — ${serviceLabel}` : ""}`
          : `Demande de location${serviceLabel ? ` — ${serviceLabel}` : ""}`;
      return `${base}${need ? `. Besoin : ${need}` : ""}${
        date ? `, pour le ${date}` : ""
      }. Merci de me recontacter avec une proposition.`;
    },
    honeypotLabel: "Ne pas remplir",
    submit: "Être recontacté",
    sending: "Envoi en cours…",
    successTitle: "Demande envoyée",
    successMessage:
      "Merci ! Votre demande est bien reçue. Notre équipe vous recontacte rapidement avec une proposition adaptée.",
    errorMessage:
      "Une erreur est survenue. Réessayez ou écrivez-nous directement.",
    privacyNote:
      "Vos informations restent confidentielles et ne servent qu'à traiter votre demande. Aucune transmission à des tiers.",
    consentNote:
      "En envoyant ce formulaire, vous acceptez d'être recontacté au sujet de votre demande.",
    requiredFirstName: "Le prénom est requis (2 caractères minimum)",
    requiredLastName: "Le nom est requis (2 caractères minimum)",
    requiredEmail: "Un email valide est requis",
    invalidPhone: "Format de téléphone invalide",
  },
  en: {
    eyebrow: "Fast reply — no obligation",
    heading: (context, serviceLabel) =>
      context === "chauffeur"
        ? serviceLabel
          ? `Book your private chauffeur — ${serviceLabel}`
          : "Book your private chauffeur"
        : serviceLabel
          ? `Request your rental — ${serviceLabel}`
          : "Request your rental",
    subtitle: (context) =>
      context === "chauffeur"
        ? "Tell us your need (date, route or occasion): our team will get back to you shortly with a clear proposal, a professional chauffeur and a price with no surprises."
        : "Share your need and your dates: our team will get back to you shortly with the available vehicles and a clear price, with delivery available to your place of stay.",
    badge: (context) =>
      context === "chauffeur"
        ? "Professional chauffeur · French Riviera"
        : "Premium & prestige · delivery available",
    firstNameLabel: "First name",
    firstNamePlaceholder: "Your first name",
    lastNameLabel: "Last name",
    lastNamePlaceholder: "Your last name",
    emailLabel: "Email",
    emailPlaceholder: "you@email.com",
    phoneLabel: "Phone (optional)",
    phonePlaceholder: "+44 20 1234 5678",
    dateLabel: "Preferred date (optional)",
    needLabel: (context) =>
      context === "chauffeur" ? "Your need" : "Vehicle or need",
    needPlaceholder: (context) =>
      context === "chauffeur"
        ? "E.g. airport transfer, hourly hire, event…"
        : "E.g. premium SUV, convertible, duration, type of use…",
    messageLabel: "Message (optional)",
    messagePlaceholder: (context) =>
      context === "chauffeur"
        ? "Tell us your timings, number of passengers, luggage, the context…"
        : "Tell us your dates, the handover location, type of use, your preferences…",
    defaultMessage: ({ context, serviceLabel, need, date }) => {
      const base =
        context === "chauffeur"
          ? `Private chauffeur request${serviceLabel ? ` — ${serviceLabel}` : ""}`
          : `Rental request${serviceLabel ? ` — ${serviceLabel}` : ""}`;
      return `${base}${need ? `. Need: ${need}` : ""}${
        date ? `, for ${date}` : ""
      }. Please get back to me with a proposal.`;
    },
    honeypotLabel: "Do not fill",
    submit: "Get a call back",
    sending: "Sending…",
    successTitle: "Request sent",
    successMessage:
      "Thank you! Your request has been received. Our team will get back to you shortly with a tailored proposal.",
    errorMessage:
      "Something went wrong. Please try again or email us directly.",
    privacyNote:
      "Your details stay confidential and are only used to handle your request. Never shared with third parties.",
    consentNote:
      "By sending this form, you agree to be contacted about your request.",
    requiredFirstName: "First name is required (min 2 characters)",
    requiredLastName: "Last name is required (min 2 characters)",
    requiredEmail: "A valid email is required",
    invalidPhone: "Invalid phone format",
  },
  ar: {
    eyebrow: "ردّ سريع — دون أي التزام",
    heading: (context, serviceLabel) =>
      context === "chauffeur"
        ? serviceLabel
          ? `احجز سائقك الخاص — ${serviceLabel}`
          : "احجز سائقك الخاص"
        : serviceLabel
          ? `اطلب سيارتك للإيجار — ${serviceLabel}`
          : "اطلب سيارتك للإيجار",
    subtitle: (context) =>
      context === "chauffeur"
        ? "أخبرنا باحتياجك (التاريخ أو المسار أو المناسبة): يعاود فريقنا الاتصال بك سريعًا بعرض واضح، وسائق محترف، وسعر دون مفاجآت."
        : "اذكر احتياجك وتواريخك: يعاود فريقنا الاتصال بك سريعًا بالمركبات المتاحة وسعر واضح، مع إمكانية التوصيل إلى مكان إقامتك.",
    badge: (context) =>
      context === "chauffeur"
        ? "سائق محترف · الريفييرا الفرنسية"
        : "راقية وفخمة · التوصيل متاح",
    firstNameLabel: "الاسم الأول",
    firstNamePlaceholder: "اسمك الأول",
    lastNameLabel: "اسم العائلة",
    lastNamePlaceholder: "اسم عائلتك",
    emailLabel: "البريد الإلكتروني",
    emailPlaceholder: "you@email.com",
    phoneLabel: "الهاتف (اختياري)",
    phonePlaceholder: "+33 6 12 34 56 78",
    dateLabel: "التاريخ المطلوب (اختياري)",
    needLabel: (context) =>
      context === "chauffeur" ? "احتياجك" : "المركبة أو الاحتياج",
    needPlaceholder: (context) =>
      context === "chauffeur"
        ? "مثال: نقل من المطار، إتاحة بالساعة، فعالية…"
        : "مثال: سيارة دفع رباعي راقية، سيارة مكشوفة، المدة، نوع الاستخدام…",
    messageLabel: "رسالة (اختياري)",
    messagePlaceholder: (context) =>
      context === "chauffeur"
        ? "حدّد مواعيدك، وعدد الركّاب، والأمتعة، والسياق…"
        : "حدّد تواريخك، ومكان التسليم، ونوع الاستخدام، وتفضيلاتك…",
    defaultMessage: ({ context, serviceLabel, need, date }) => {
      const base =
        context === "chauffeur"
          ? `طلب سائق خاص${serviceLabel ? ` — ${serviceLabel}` : ""}`
          : `طلب إيجار${serviceLabel ? ` — ${serviceLabel}` : ""}`;
      return `${base}${need ? `. الاحتياج: ${need}` : ""}${
        date ? `، بتاريخ ${date}` : ""
      }. يُرجى معاودة الاتصال بي مع عرض مناسب.`;
    },
    honeypotLabel: "يُرجى عدم التعبئة",
    submit: "أن يُعاد الاتصال بي",
    sending: "جارٍ الإرسال…",
    successTitle: "تم إرسال الطلب",
    successMessage:
      "شكرًا لك! تم استلام طلبك. يعاود فريقنا الاتصال بك سريعًا بعرض مناسب.",
    errorMessage: "حدث خطأ. حاول مجددًا أو راسلنا مباشرةً.",
    privacyNote:
      "تبقى معلوماتك سرّية ولا تُستخدم إلا لمعالجة طلبك. لا تُشارَك مع أي طرف ثالث.",
    consentNote:
      "بإرسالك هذا النموذج، فإنك توافق على أن يُعاد الاتصال بك بشأن طلبك.",
    requiredFirstName: "الاسم الأول مطلوب (حرفان على الأقل)",
    requiredLastName: "اسم العائلة مطلوب (حرفان على الأقل)",
    requiredEmail: "بريد إلكتروني صالح مطلوب",
    invalidPhone: "صيغة الهاتف غير صحيحة",
  },
};
